import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { clearButtonBusy, setButtonLoading } from "../lib/button_loading"
import { syncGateReason } from "../lib/gate_reason"

// BUTTON-4: why the email button rests after a sent link.
const EMAIL_SENT_REASON = "Sign-in link sent. Check that inbox."

// Wizard Step-3 duplicate-account recovery card. Armed by npn-validate:recovery
// (dispatched when validate_npn returns masked recovery contacts); every action
// re-sends the typed NPN — the server re-resolves the holder each time.
//
// A verified SMS code signs the agent straight into the holder account (the
// server folds this half-finished signup into it, switching the login email to
// the one typed here when the checkbox is on) and answers with the post-login
// URL. The same redirect_url key carries the password-reset hand-off the
// server falls back to for MFA-protected holders.
export default class extends Controller {
  static targets = ["card", "emailText", "phoneText", "smsSection", "smsToggle",
                    "codeRow", "codeInput", "status", "emailButton", "verifyButton", "switchEmail"]
  static values  = {
    emailLinkUrl: String, smsStartUrl: String, smsVerifyUrl: String,
    // Button copy for the two card states: nothing sent yet (the manual
    // send), and validate_npn already auto-sent the link when it armed the
    // card (#11601), where the same button is now a resend.
    sendLabel: { type: String, default: "Email me a sign-in link" },
    resendLabel: { type: String, default: "Resend sign-in link" }
  }

  show(event) {
    this.npn = event.detail.npn
    this.reset()
    const maskedEmail = event.detail.masked_email || ""
    this.emailTextTarget.textContent = maskedEmail
    this.phoneTextTarget.textContent = event.detail.masked_phone || ""
    this.smsSectionTarget.classList.toggle("d-none", !event.detail.sms_available)
    if (event.detail.email_sent) this.showAutoSent(maskedEmail)
    this.cardTarget.classList.remove("d-none")
  }

  // The server sent the reset link the moment it detected the collision, so
  // the card opens on the confirmation rather than on a button the user has
  // to find. The button stays enabled as a resend — the per-holder cooldown
  // server-side answers an eager second click with its own message.
  showAutoSent(maskedEmail) {
    this.setStatus(`We just emailed a sign-in link to ${maskedEmail}. Check that inbox, then sign back in.`, true)
    this.emailButtonTarget.textContent = this.resendLabelValue
  }

  // npn-validate:recovery-clear — the latest validate attempt carried no
  // recovery data, so whatever this card is showing belongs to a PREVIOUS
  // NPN. Hide it and drop the captured NPN so no button can still email/text
  // that stale number's holder.
  hide() {
    this.npn = null
    this.cardTarget.classList.add("d-none")
    this.reset()
  }

  // BUTTON-8: each request button keeps its label while it runs (shared
  // spinner + aria-busy); a sent link then rests the email button, disabled
  // WITH its reason (BUTTON-4).
  async sendEmailLink() {
    setButtonLoading(this.emailButtonTarget, true)
    const data = await this.request(this.emailLinkUrlValue, { npn_id: this.npn })
    setButtonLoading(this.emailButtonTarget, false)
    if (!data) return
    this.setStatus(data.message, data.success)
    if (data.success) syncGateReason(this.emailButtonTarget, { ready: false, reason: EMAIL_SENT_REASON })
  }

  async startSms() {
    if (this.hasSmsToggleTarget) setButtonLoading(this.smsToggleTarget, true)
    const data = await this.request(this.smsStartUrlValue, { npn_id: this.npn })
    if (this.hasSmsToggleTarget) setButtonLoading(this.smsToggleTarget, false)
    if (!data) return
    this.setStatus(data.message, data.success)
    if (data.success) this.codeRowTarget.classList.remove("d-none")
  }

  async verifySms() {
    const code = this.codeInputTarget.value.trim()
    if (!code) return
    this.setVerifyBusy(true)
    const body = { npn_id: this.npn, code, switch_email: this.switchEmailRequested() }
    const data = await this.request(this.smsVerifyUrlValue, body)
    if (!data) {
      this.setVerifyBusy(false)
      return
    }
    if (!data.success) {
      this.setVerifyBusy(false)
      this.setStatus(data.message || "Invalid or expired code.", false)
      return
    }
    this.setStatus(data.message || "Verified! Signing you in…", true)
    if (data.redirect_url) {
      this.navigateTo(data.redirect_url)
    } else {
      // The server always sends one today; if it ever doesn't, the request
      // still succeeded — say so rather than reuse the invalid-code copy.
      console.warn("npn-recovery: sms_verify succeeded without a redirect_url")
      this.setVerifyBusy(false)
    }
  }

  // "1"/"0" rather than a boolean: the body goes out as form params, where a
  // boolean would stringify to "true"/"false" — both truthy to a careless
  // reader. Rails casts either form the same way; this keeps the wire explicit.
  switchEmailRequested() {
    return this.hasSwitchEmailTarget && this.switchEmailTarget.checked ? "1" : "0"
  }

  // The verify round-trip merges accounts server-side; a double click must not
  // race two of them.
  setVerifyBusy(busy) {
    if (this.hasVerifyButtonTarget) setButtonLoading(this.verifyButtonTarget, busy)
  }

  // jsdom can't spy window.location.assign; an instance seam can be. Tests stub this.
  navigateTo(url) {
    window.location.assign(url)
  }

  async request(url, body) {
    try {
      const response = await post(url, { body, responseKind: "json" })
      return await response.json
    } catch {
      this.setStatus("Request failed. Please try again.", false)
      return null
    }
  }

  // Built from a plain server string, not HTML: textContent only, never innerHTML.
  setStatus(message, ok) {
    this.statusTarget.textContent = message || ""
    this.statusTarget.className = ok
      ? "gl-onboarding-npn-message gl-onboarding-npn-message--valid"
      : "gl-onboarding-npn-message gl-onboarding-npn-message--invalid"
  }

  reset() {
    this.statusTarget.textContent = ""
    this.statusTarget.className = "gl-onboarding-npn-message d-none"
    this.codeRowTarget.classList.add("d-none")
    this.codeInputTarget.value = ""
    clearButtonBusy(this.emailButtonTarget)
    syncGateReason(this.emailButtonTarget, { ready: true })
    this.emailButtonTarget.textContent = this.sendLabelValue
    this.setVerifyBusy(false)
    if (this.hasSwitchEmailTarget) this.switchEmailTarget.checked = true
  }
}
